// 지각 속도 (사물지각력) — 두 줄이 같은지 다른지 빠르게 판단한다.
// 다를 때는 비슷하게 생긴 글자 하나를 바꾸거나(O↔Q, 밝↔밟, ◇↔◆) 이웃한 두 글자의 순서를 바꾼다.
// 레벨이 오르면 길어지고, 헷갈리는 글자 묶음이 섞이고, 한 문제 제한 시간이 짧아진다.
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export interface PerceptQuestion extends ChoiceQuestion { left: string; right: string; same: boolean }

export const PERCEPT_COUNT = 20;
export const PERCEPT_CHOICES = ['같다', '다르다'];

/** 서로 헷갈리기 쉬운 글자 묶음 — 같은 묶음 안에서 바꿔치기한다 */
export const CONFUSE: Record<string, string[]> = {
  digit: ['6890', '1742', '35'],
  latin: ['OQCGD', 'BRP8', 'EF', 'MNW', 'UV', 'KX'],
  hangul: ['갑값감강', '밝밟발밤', '를룰릉릴', '했햇헀', '꽃꽂꼿', '넓넒넘'],
  symbol: ['◇◆◈', '○●◎', '△▲▽', '□■▣', '←↑→↓', '♤♠♧♣'],
};
const SETS: Record<string, string> = {
  digit: '0123456789',
  latin: 'ABCDEFGHKMNOPQRSUVWX8',
  hangul: '갑값감강밝밟발밤를룰릉릴했햇꽃꽂넓넒넘가나다라',
  symbol: '◇◆◈○●◎△▲▽□■▣←↑→↓♤♠♧♣',
};

function partner(r: Rng, ch: string, kind: string): string | null {
  const group = CONFUSE[kind].find((g) => [...g].includes(ch));
  if (!group) return null;
  const others = [...group].filter((c) => c !== ch);
  return others.length ? pick(r, others) : null;
}

export function makePerceptRound(level: number, seed: number): PerceptQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const len = 4 + Math.floor(lv * 0.6);
  const limitSec = Math.round((7 - 0.42 * (lv - 1)) * 10) / 10;
  const kinds = lv <= 2 ? ['digit', 'latin'] : lv <= 5 ? ['digit', 'latin', 'hangul'] : ['digit', 'latin', 'hangul', 'symbol'];
  const sames = shuffle(rng, Array.from({ length: PERCEPT_COUNT }, (_, i) => i < PERCEPT_COUNT / 2));
  return sames.map((same) => {
    const kind = pick(rng, kinds);
    const chars = [...SETS[kind]];
    const left = Array.from({ length: len }, () => pick(rng, chars));
    const right = [...left];
    if (!same) {
      const swap = lv >= 4 && rng() < 0.3;
      if (swap) {
        const i = int(rng, 0, len - 2);
        if (right[i] === right[i + 1]) right[i + 1] = right[i] === chars[0] ? chars[1] : chars[0];
        [right[i], right[i + 1]] = [right[i + 1], right[i]];
      } else {
        // 헷갈리는 짝이 있는 자리를 골라 바꾼다 (없으면 아무 다른 글자로)
        const spots = shuffle(rng, left.map((_, i) => i));
        const i = spots.find((k) => partner(rng, left[k], kind)) ?? spots[0];
        right[i] = partner(rng, left[i], kind) ?? chars.find((c) => c !== left[i])!;
      }
    }
    const a = left.join(''), b = right.join('');
    return { left: a, right: b, same: a === b, answer: a === b ? 0 : 1, n: 2, limitSec, fast: true };
  });
}
